import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import type { ChartOfAccountsId, ChartTemplate, ImportChartResult } from "@/types/camping";

export interface ChartOfAccountsImportPanelProps {
  onImported: () => void;
  disabled?: boolean;
}

export function ChartOfAccountsImportPanel({
  onImported,
  disabled = false,
}: ChartOfAccountsImportPanelProps) {
  const [charts, setCharts] = useState<ChartTemplate[]>([]);
  const [selectedChart, setSelectedChart] = useState<ChartOfAccountsId>("skr03");
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadCharts = useCallback(() => {
    setLoading(true);
    api
      .get<ChartTemplate[]>("/api/v1/accounts/chart-templates")
      .then((items) => {
        setCharts(items);
        const first = items[0];
        if (first) {
          setSelectedChart(first.id);
        }
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadCharts();
  }, [loadCharts]);

  async function onImport() {
    setError(null);
    setSuccess(null);
    setImporting(true);
    try {
      const result = await api.post<ImportChartResult>("/api/v1/accounts/import-chart", {
        chart_id: selectedChart,
      });
      setSuccess(result.message);
      onImported();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import fehlgeschlagen");
    } finally {
      setImporting(false);
    }
  }

  const selected = charts.find((c) => c.id === selectedChart);

  return (
    <Card title="Kontenrahmen vorauswählen" bodyClassName="space-y-4">
      <p className="text-sm text-label-secondary">
        Wählen Sie SKR03 oder SKR04, um typische Erlöskonten für Campingplätze vorzufüllen.
        Anschließend können Sie jedes Konto einzeln anpassen oder weitere Konten manuell
        anlegen.
      </p>
      {loading ? <p className="text-sm text-label-secondary">Kontenrahmen laden…</p> : null}
      {charts.length > 0 ? (
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
          <label className="flex min-w-[12rem] flex-col gap-1 text-sm">
            Kontenrahmen
            <select
              className="eh-select-input"
              value={selectedChart}
              disabled={disabled || importing}
              onChange={(e) => setSelectedChart(e.target.value as ChartOfAccountsId)}
            >
              {charts.map((chart) => (
                <option key={chart.id} value={chart.id}>
                  {chart.label} ({chart.account_count} Konten)
                </option>
              ))}
            </select>
          </label>
          <Button
            type="button"
            className="w-full sm:w-auto"
            loading={importing}
            disabled={disabled || importing}
            onClick={() => void onImport()}
          >
            Konten übernehmen
          </Button>
        </div>
      ) : null}
      {selected ? (
        <p className="text-xs text-label-tertiary">{selected.description}</p>
      ) : null}
      {success ? <p className="text-sm text-status-green">{success}</p> : null}
      {error ? <p className="text-sm text-status-red">{error}</p> : null}
    </Card>
  );
}
